import React, { useContext, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'umi';
import classNames from 'classnames';
import { commonValueFormatter } from '@/pages/dashboard/utils/utils';
import { FilterContext } from '../PageContainer/context';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { formatPlatformList } from '../MetricCard/utils';
import { ISelectorState } from '../../types/dashboard';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { LANGUAGE_EN } from '@home/_config/config';
import CommonBubble from '@home/components/CommonBubble';
import type { 
  IPlatformCardItemProps, 
  IDimensionContext, 
  ICustomFilterItem,
  IPlatformItem,
  ISelectorItem,
  IProgressItem,
  ICatalogSelectedInfo,
} from './type';
import {
  createProgressListByDimension,
  updateProgressListByDimension,
  formatPercentage,
  resolvePlatformIconByName,
} from './_utils';
import techlandBackCatalogueIcon from '@home/assets/svg/studio/home/techland_back_catalogue_kpi_title_icon.svg';
import techlandSpecialDealsIcon from '@home/assets/svg/studio/home/techland_special_deals_kpi_title_icon.svg';

const BUILTIN_BREAKDOWN_ICON_MAP: Record<string, string> = {
  Backcatalog: techlandBackCatalogueIcon,
  'Special Deals': techlandSpecialDealsIcon,
};

interface BreakdownProgressItemProps {
  progress: IProgressItem;
  isEN: boolean;
  breakdownTooltipMap?: Record<string, { en: string; cn: string }>;
  dataSource: IPlatformCardItemProps['dataSource'];
  showShare: boolean;
  resolveBreakdownIcon: (name: string, title: string) => string | undefined;
}

function BreakdownProgressItem({
  progress,
  isEN,
  breakdownTooltipMap,
  dataSource,
  showShare,
  resolveBreakdownIcon,
}: BreakdownProgressItemProps): JSX.Element {
  const { hasData, share, isOpacity, disableIcon, icon, title, color, isDefault, isBlackImg, num, name: itemName } = progress || {};

  const isDisabled = hasData === false;
  const { type, decimal, formatRule, dimension } = dataSource || {};
  const percentage = share ? Number(share) * 100 : 0;
  const shareStr = share !== '' ? ` (${formatPercentage(percentage)})` : '';

  let value: string | number = '-';
  if (num !== '-') {
    if (formatRule) {
      value = dataFormat({ value: num, formatRule }) || '-';
    } else {
      value = commonValueFormatter(type, num, decimal) || '-';
    }
  }

  const overrideIcon = resolveBreakdownIcon?.(itemName, title);
  const resolvedIcon = overrideIcon || (isDisabled ? disableIcon : icon);
  const hasOverrideIcon = !!overrideIcon;

  const breakdownTooltip = isEN
    ? breakdownTooltipMap?.[title]?.en || breakdownTooltipMap?.[itemName]?.en
    : breakdownTooltipMap?.[title]?.cn || breakdownTooltipMap?.[itemName]?.cn;

  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={classNames('dbd-newdaily-progress', {
        opacity: isOpacity && !hasOverrideIcon,
        disable: isDisabled,
      })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        className={classNames('dbd-newdaily-progress-img', {
          'dbd-img-country-dark': dimension === 'country',
          'dbd-newdaily-progress-img-dark': !hasOverrideIcon && (isBlackImg || num === '-' || isDefault),
          'dbd-newdaily-progress-img-catalog': dimension === 'catalog',
        })}
        src={resolvedIcon}
        alt={title}
      />
      <div className="dbd-newdaily-progress-data">
        <div className="dbd-newdaily-progress-flex">
          <CommonBubble
            visible={hovered}
            trigger="empty"
            content={breakdownTooltip}
            placement="top"
            dark
            arrowPointAtCenter
          >
            <div className="dbd-newdaily-progress-title">
              <span>{title}</span>
            </div>
          </CommonBubble>
          <div className="dbd-newdaily-progress-num">
            {value} {(share !== '' && showShare) && shareStr}
          </div>
        </div>
        <div className="dbd-newdaily-progress-box">
          <div
            className="dbd-newdaily-progress-bg"
            style={{ width: `${percentage}%`, background: color }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PlatformCardItem(props: IPlatformCardItemProps) {
  const { dataSource, platFormData, showShare=true, breakdownIconConfig, gameList, breakdownTooltipMap } = props;
  const contextObj = useContext(FilterContext);
  const { filterVal, extraSelector: contextExtraSelector } = contextObj || {};
  
  // 使用类型断言避免解构时的类型错误，同时保持类型安全
  const systemId = (filterVal?.system as string[]) || ['255'];
  const region = filterVal?.region;
  const customFilters = (filterVal as Record<string, unknown>)?.custom_filters as ICustomFilterItem[] | undefined;
  const filterCatalog = (filterVal as Record<string, unknown>)?.filterCatalog as ICatalogSelectedInfo | undefined;
  
  const { locale } = useLocal();
  const isEN = locale === LANGUAGE_EN;

  const { gameCode = '', globalSelector } = useSelector(
    (state: ISelectorState) => ({
      gameCode: state?.dashboard?.currentGame?.game_code,
      globalSelector: state?.dashboardSetting?.selector,
    }),
    shallowEqual
  );

  // 合并全局 selector 和 extraSelector
  const selector = useMemo(() => {
    if (!contextExtraSelector) {
      return globalSelector;
    }
    return {
      ...globalSelector,
      ...contextExtraSelector,
    };
  }, [globalSelector, contextExtraSelector]);

  const countryList = selector?.country_list || [];

  const platformList = useMemo(() => {
    const osList = (selector?.os_list ?? []).filter((item: IPlatformItem) => item?.os_id !== '255');
    const adList = (selector?.ad_format ?? []).filter(
      (item: ISelectorItem) => item?.code !== '255'
    );
    return [...osList, ...adList];
  }, [selector]);

  // 提取筛选器中选中的国家/地区（限制前 5 个）
  const filterRegions = useMemo(() => {
    // 优先使用 markets（具体国家列表）
    const markets = region?.markets || [];
    const filteredMarkets = markets.filter((m: string) => m !== '255').slice(0, 5);
    
    if (filteredMarkets.length > 0) {
      return filteredMarkets;
    }
    
    // 如果没有 markets，尝试使用 regions（地区代码）
    const regions = region?.regions || [];
    const filteredRegions = regions.filter((r: string) => r !== '255').slice(0, 5);
    
    return filteredRegions.length > 0 ? filteredRegions : undefined;
  }, [region]);

  // 提取筛选器中选中的 UA 渠道（限制前 5 个）
  const filterUaNetworks = useMemo(() => {
    // 从 custom_filters 中查找 key 为 'ua_network' 的筛选项
    const uaNetworkFilter = customFilters?.find(
      (filter: ICustomFilterItem) => filter?.key === 'ua_network'
    );
    const filterList = uaNetworkFilter?.filter_list || [];
    
    // 过滤掉 '255' 并限制前 5 个
    const filteredList = filterList.filter((item: string) => item !== '255').slice(0, 5);
    
    return filteredList.length > 0 ? filteredList : undefined;
  }, [customFilters]);

  // 提取筛选器中选中的 UA 渠道（限制前 5 个）
  const filterAdFormat = useMemo(() => {
    // 从 custom_filters 中查找 key 为 'ad_format' 的筛选项
    const adFormatFilter = customFilters?.find(
      (filter: ICustomFilterItem) => filter?.key === 'ad_format'
    );
    const filterList = adFormatFilter?.filter_list || [];
    
    // 过滤掉 '255' 并限制前 5 个
    const filteredList = filterList.filter((item: string) => item !== '255').slice(0, 5);
    
    return filteredList.length > 0 ? filteredList : undefined;
  }, [customFilters]);
  

  const dimensionContext: IDimensionContext = useMemo(
    () => ({
      selector,
      countryList,
      isEN,
      gameCode,
      systemId,
      platformList,
      ...(filterRegions && { filterRegions }),
      ...(filterUaNetworks && { filterUaNetworks }),
      ...(filterAdFormat && { filterAdFormat }),
      ...(filterCatalog && { filterCatalog }),
    }),
    [selector, countryList, isEN, gameCode, systemId, platformList, filterRegions, filterUaNetworks, filterAdFormat, filterCatalog]
  );

  const resolveBreakdownIcon = useMemo(() => {
    const { fallbackImageMap, gameListMatchField = 'game_name', gameListImageField = 'cover' } = breakdownIconConfig || {};
    const normalize = (str: string): string => str?.toLowerCase()?.replace(/[^a-z0-9]/g, '') || '';
    return (name: string, title: string): string | undefined => {
      if (BUILTIN_BREAKDOWN_ICON_MAP[title]) return BUILTIN_BREAKDOWN_ICON_MAP[title];
      if (BUILTIN_BREAKDOWN_ICON_MAP[name]) return BUILTIN_BREAKDOWN_ICON_MAP[name];
      if (fallbackImageMap?.[title]) return fallbackImageMap[title];
      if (fallbackImageMap?.[name]) return fallbackImageMap[name];
      if (gameList?.length) {
        const normalizedTitle = normalize(title);
        const normalizedName = normalize(name);
        const matched = gameList.find((g: any) => {
          const fieldVal = g?.[gameListMatchField];
          if (!fieldVal) return false;
          return fieldVal === title || fieldVal === name || normalize(fieldVal) === normalizedTitle || normalize(fieldVal) === normalizedName;
        });
        if (matched?.[gameListImageField]) return matched[gameListImageField];
      }
      return undefined;
    };
  }, [breakdownIconConfig, gameList]);

  const disableProgressList = useMemo(() => {
    const { dimension = 'os', defaultSelectedList } = dataSource || {};
    return createProgressListByDimension(dimension, defaultSelectedList, dimensionContext);
  }, [dataSource, dimensionContext]);

  const { progressList = [] } = useMemo(() => {
    const { dimension = 'os' } = dataSource || {};

    // 非 os 维度（country/ad_format/ua_network/catalog）的 platform.name 是各自维度的标识（国家编码、广告名称等），
    // 无法与 systemId（OS 平台 ID）匹配，强制设为 ['255'] 跳过 OS 筛选，避免数据被误过滤为 '-'
    const effectiveSystemId = dimension !== 'os' ? ['255'] : systemId;
    const { progressList: oldProgressList } = formatPlatformList(
      disableProgressList,
      platFormData,
      effectiveSystemId,
      gameCode
    );

    // catalog 维度：将没有数据的 catalog 项补充到列表末尾，保留图片和名称，数据显示 '-'
    let mergedProgressList: IProgressItem[] = oldProgressList as IProgressItem[];
    if (dimension === 'catalog' && disableProgressList?.length > 0 && platFormData?.length > 0) {
      const existingNames = new Set(
        (oldProgressList || []).map((item: any) => item?.name as string)
      );
      const missingItems: IProgressItem[] = disableProgressList
        .filter((item) => !existingNames.has(item?.name))
        .map((item) => ({
          ...item,
          hasData: true,
          num: '-' as const,
          share: '',
          color: '',
        }));
      mergedProgressList = [...((oldProgressList || []) as IProgressItem[]), ...missingItems];
    }

    const updatedProgressList = updateProgressListByDimension(
      dimension,
      mergedProgressList,
      dimensionContext
    );

    if (!breakdownIconConfig?.defaultItems?.length) return { progressList: updatedProgressList };

    const existingNames = new Set(
      (updatedProgressList || []).map((item) => (item?.name || item?.title || '')?.toLowerCase()),
    );
    const existingTitles = new Set(
      (updatedProgressList || []).map((item) => (item?.title || '')?.toLowerCase()),
    );
    const missingDefaults: IProgressItem[] = breakdownIconConfig.defaultItems
      .filter((d) => !existingNames.has(d.name?.toLowerCase()) && !existingTitles.has(d.name?.toLowerCase()))
      .map((d) => {
        const resolved = d.icon ? null : resolvePlatformIconByName(d.title || d.name);
        return {
          isOpacity: resolved?.isOpacity ?? false,
          isBlackImg: resolved?.isBlack ?? false,
          disableIcon: d.icon || resolved?.disableIcon || '',
          icon: d.icon || resolved?.icon || '',
          title: d.title || d.name,
          hasData: true,
          isSelected: true,
          num: '-' as const,
          share: '',
          name: d.name,
          color: '',
        };
      });

    return { progressList: [...(updatedProgressList || []), ...missingDefaults] };
  }, [platFormData, systemId, disableProgressList, gameCode, dataSource, dimensionContext, breakdownIconConfig]);

  return (
    <>
      {progressList?.map((progress, index) => (
        <BreakdownProgressItem
          key={index}
          progress={progress}
          isEN={isEN}
          breakdownTooltipMap={breakdownTooltipMap}
          dataSource={dataSource}
          showShare={showShare}
          resolveBreakdownIcon={resolveBreakdownIcon}
        />
      ))}
    </>
  );
}